import { collection, getDocs } from "firebase/firestore";
import { db } from "../firebase";

export const distances = ["Triathlon", "Duathlon", "Aquabike", "Aquathlon", "Mini Sprint", "Itsy Bitsy"];
export const raceTabs = ["SpringSprint", "SDIT", "SolanaBeach", "ChulaVistaChallenge", "SDTriClassic", "MissionBayTriathlon"];
const sprintPoints: { [key: number]: number } = { 1: 10, 2: 9, 3: 8, 4: 7, 5: 6, 6: 5, 7: 4, 8: 3, 9: 2, 10: 1 };
const internationalPoints: { [key: number]: number } = { 1: 15, 2: 13, 3: 12, 4: 11, 5: 9, 6: 7, 7: 6, 8: 5, 9: 3, 10: 1 };

function toProperCase(str: string): string {
  if (!str) return "";
  return str.toLowerCase().replace(/(^|\s)\w/g, (letter) => letter.toUpperCase());
}

function normalizeDOB(dob: string | number | Date | null | undefined): string | null {
  if (dob === null || dob === undefined) {
    console.warn("DOB is null or undefined");
    return null;
  }
  let date: Date;
  try {
    if (dob instanceof Date) {
      date = dob;
    } else if (typeof dob === "number") {
      date = new Date((dob - 25569) * 86400 * 1000);
    } else {
      // Try YYYY-MM-DD
      let parts = dob.split("-");
      if (parts.length === 3) {
        const [year, month, day] = parts.map(Number);
        if (!isNaN(month) && !isNaN(day) && !isNaN(year) && month >= 1 && month <= 12 && day >= 1 && day <= 31) {
          date = new Date(year, month - 1, day);
        } else {
          console.warn(`Invalid DOB values (YYYY-MM-DD): ${dob}`);
          return null;
        }
      } else {
        // Try MM/DD/YYYY
        parts = dob.split("/");
        if (parts.length === 3) {
          const [month, day, year] = parts.map(Number);
          if (!isNaN(month) && !isNaN(day) && !isNaN(year) && month >= 1 && month <= 12 && day >= 1 && day <= 31) {
            date = new Date(year, month - 1, day);
          } else {
            console.warn(`Invalid DOB values (MM/DD/YYYY): ${dob}`);
            return null;
          }
        } else {
          console.warn(`Invalid DOB format: ${dob}`);
          return null;
        }
      }
    }
    if (isNaN(date.getTime())) {
      console.warn(`Invalid date parsed from DOB: ${dob}`);
      return null;
    }
    return `${(date.getMonth() + 1).toString().padStart(2, "0")}/${date.getDate().toString().padStart(2, "0")}/${date.getFullYear()}`;
  } catch (error) {
    console.error(`Error normalizing DOB: ${dob}`, error);
    return null;
  }
}

interface ParticipantRow {
  RACE_NAME: string;
  FIRST_NAME: string;
  LAST_NAME: string;
  DOB?: string;
  PRIMARY_BRACKET: string;
  PRIMARY_BRACKET_RANK: string;
  EMAIL?: string;
  event: string;
  GUN_FINISH_TIME?: string;
  CITY?: string;
  REGION_CODE?: string;
  GENDER?: string;
  [key: string]: any;
}

export async function calculateRankings(): Promise<{ [distance: string]: any }> {
  console.log("Starting calculateRankings...");
  const allData: ParticipantRow[] = [];
  for (const tab of raceTabs) {
    try {
      const snapshot = await getDocs(collection(db, tab));
      console.log(`Fetched ${snapshot.size} documents from ${tab}`);
      if (snapshot.empty) {
        console.warn(`No documents found in ${tab}`);
      }
      snapshot.forEach((doc) => {
        const row = doc.data();
        if (!row.RACE_NAME || !row.FIRST_NAME || !row.LAST_NAME || !row.PRIMARY_BRACKET || !row.PRIMARY_BRACKET_RANK) {
          console.warn(`Skipping document in ${tab} due to missing required fields:`, JSON.stringify(row, null, 2));
          return;
        }
        console.log(`Document in ${tab}:`, JSON.stringify(row, null, 2));
        allData.push({ ...row, event: tab } as ParticipantRow);
      });
    } catch (error) {
      console.error(`Error fetching data from ${tab}:`, error);
    }
  }
  console.log("Total documents collected:", allData.length);

  if (allData.length === 0) {
    console.warn("No data found in any race collections. Returning empty rankings.");
    return distances.reduce((acc, d) => ({ ...acc, [d]: {} }), {});
  }

  const participantMapByDistance: { [key: string]: any } = {};
  distances.forEach((distance) => (participantMapByDistance[distance] = {}));

  allData.forEach((row) => {
    const { RACE_NAME, FIRST_NAME, LAST_NAME, DOB, PRIMARY_BRACKET_RANK, PRIMARY_BRACKET, event, EMAIL, GUN_FINISH_TIME, CITY, REGION_CODE, GENDER } = row;
    if (!FIRST_NAME || !LAST_NAME || !PRIMARY_BRACKET_RANK || isNaN(parseInt(PRIMARY_BRACKET_RANK)) || !RACE_NAME || !event) {
      console.warn("Skipping invalid row:", JSON.stringify(row, null, 2));
      return;
    }

    const normalizedDob = normalizeDOB(DOB);
    let uniqueKey = `${FIRST_NAME.trim().toLowerCase()}|${LAST_NAME.trim().toLowerCase()}|${PRIMARY_BRACKET.trim().toLowerCase()}|${
      normalizedDob || EMAIL?.trim().toLowerCase() || `unknown_${FIRST_NAME.trim().toLowerCase()}_${LAST_NAME.trim().toLowerCase()}@example.com`
    }`.replace(/\s+/g, "");
    console.log(`Processing participant: ${uniqueKey}`);

    const raceNameLower = RACE_NAME.toLowerCase();
    let distanceKey = "";
    let isInternational = false;
    if (raceNameLower.includes("triathlon") && !raceNameLower.includes("mini") && !raceNameLower.includes("itsy")) {
      distanceKey = "Triathlon";
      isInternational = raceNameLower.includes("international");
    } else if (raceNameLower.includes("duathlon")) {
      distanceKey = "Duathlon";
      isInternational = false;
    } else if (raceNameLower.includes("aquabike")) {
      distanceKey = "Aquabike";
      isInternational = raceNameLower.includes("international");
    } else if (raceNameLower.includes("aquathlon")) {
      distanceKey = "Aquathlon";
      isInternational = false;
    } else if (raceNameLower.includes("mini") || raceNameLower.includes("mini-sprint")) {
      distanceKey = "Mini Sprint";
      isInternational = false;
    } else if (raceNameLower.includes("itsy") || raceNameLower.includes("itsy-bitsy")) {
      distanceKey = "Itsy Bitsy";
      isInternational = false;
    }
    if (!distanceKey) {
      console.warn(`Unknown race name: ${RACE_NAME} in event ${event}`);
      return;
    }

    const place = parseInt(PRIMARY_BRACKET_RANK);
    const points = place <= 10 ? (isInternational ? internationalPoints[place] || 0 : sprintPoints[place] || 0) : 0;

    if (!participantMapByDistance[distanceKey][uniqueKey]) {
      participantMapByDistance[distanceKey][uniqueKey] = {
        firstName: toProperCase(FIRST_NAME),
        lastName: toProperCase(LAST_NAME),
        primaryBracket: PRIMARY_BRACKET,
        totalPoints: 0,
        eventCount: 0,
        events: {},
        eventDetails: [],
        city: CITY ? toProperCase(CITY) : "N/A",
        regionCode: REGION_CODE ? REGION_CODE.toUpperCase() : "N/A",
        email: EMAIL || "N/A",
        gender: GENDER || "N/A",
        dob: DOB || null,
      };
    }

    const participant = participantMapByDistance[distanceKey][uniqueKey];
    participant.events[event] = points;
    participant.totalPoints += points;
    if (points > 0) participant.eventCount += 1;

    participant.eventDetails.push({
      event,
      time: GUN_FINISH_TIME || "N/A",
      rank: PRIMARY_BRACKET_RANK,
      points,
    });
  });

  const rankings: { [distance: string]: any } = {};
  distances.forEach((distance) => {
    const bracketMap: any = {};
    Object.entries(participantMapByDistance[distance]).forEach(([key, data]: any) => {
      const bracket = data.primaryBracket || "Unknown";
      if (!bracketMap[bracket]) bracketMap[bracket] = [];
      bracketMap[bracket].push({
        name: `${data.firstName} ${data.lastName}`,
        totalPoints: data.totalPoints,
        eventCount: data.eventCount,
        events: data.events,
        eventDetails: data.eventDetails,
        city: data.city,
        regionCode: data.regionCode,
        email: data.email,
        gender: data.gender,
        dob: data.dob,
      });
    });

    const sortedBrackets = Object.keys(bracketMap).sort((a, b) => {
      const getAgeGroup = (bracket: string) => {
        const match = bracket.match(/(\d+-\d+|\d+\+)/);
        if (match) {
          const ages = match[0].split("-");
          return parseInt(ages[0]);
        }
        return Infinity;
      };
      const ageA = getAgeGroup(a);
      const ageB = getAgeGroup(b);
      if (ageA !== ageB) return ageA - ageB;
      return a.includes("Female") ? -1 : 1;
    });

    const sortedBracketMap: any = {};
    sortedBrackets.forEach((bracket) => {
      const sortedParticipants = bracketMap[bracket].sort((a: any, b: any) => b.totalPoints - a.totalPoints || b.eventCount - a.eventCount);
      let currentRank = 1;
      let prevPoints = sortedParticipants[0]?.totalPoints;
      sortedParticipants.forEach((participant: any, index: number) => {
        if (index > 0 && participant.totalPoints < prevPoints) {
          currentRank = index + 1;
        }
        participant.rank = currentRank;
        prevPoints = participant.totalPoints;
      });
      sortedBracketMap[bracket] = sortedParticipants;
    });

    rankings[distance] = sortedBracketMap;
    console.log(`Rankings for ${distance}:`, JSON.stringify(sortedBracketMap, null, 2));
  });

  console.log("Final rankings:", JSON.stringify(rankings, null, 2));
  return rankings;
}